import { FormControl, FormLabel, Stack, Switch, Text, useDisclosure } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DraftRepresentative, IdentityType } from '@blockpulse3/graphql/hooks';
import { SwitchIdentityType, SwitchIdentityTypeChange } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { draftRepresentativeDefaultValues } from '../../provider/schema';
import { LegalRepresentantInformationsForm } from './LegalRepresentantInformationsForm';
import { NaturalRepresentantInformationsForm } from './NaturalRepresentantInformationsForm';
import { RoleIntl } from './types';

type Props = {
  id: number;
  /* ** Available roles for a representant form, must be Intl values ** */
  roles: RoleIntl[];
  /* ** Is role picker displayed ** */
  shouldDisplayRoles?: boolean;
  /* ** Are switches disabled ** */
  shouldDisabledSwitches?: boolean;
  /* ** Default form values for the Legal and Natural ** */
  defaultValues?: DraftRepresentative;
  /* ** Is the Cancel button disabled ** */
  shouldCancel?: boolean;
  /* ** Callback on identity switch ** */
  onIdentityTypeChange?: (identity: IdentityType) => void;
  /* ** Callback on user switch ** */
  onUserIdentityChange?: (isSelected: boolean) => void;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit?: (data: DraftRepresentative) => void;
};

/**
 * RepresentantInformationsForm.
 * Main form that groups the legal and natural version of it.
 * It uses a forwardRef function to submit them from the outside.
 */
export const RepresentantInformationsForm = React.forwardRef<HTMLFormElement, Props>(
  (props, ref) => {
    const {
      id,
      roles,
      shouldDisplayRoles = true,
      shouldDisabledSwitches = false,
      defaultValues = draftRepresentativeDefaultValues,
      shouldCancel = false,
      onIdentityTypeChange = noop,
      onUserIdentityChange = noop,
      onCancel = noop,
      onSubmit = noop,
    } = props;

    const i18nGeneral = useTranslations('General');

    const { user } = useUser();

    const [idType, setIdType] = useState<IdentityType>(defaultValues.type as IdentityType);

    /* ** Disclosure to control the user switch ** */
    const { isOpen, onOpen, onClose, onToggle } = useDisclosure({
      defaultIsOpen: false,
    });

    /* ** User identity change ** */
    const handleUserIdentityChange = (): void => {
      if (idType === IdentityType.COMPANY) {
        onOpen();
        setIdType(IdentityType.INDIVIDUAL);
      } else {
        onToggle();
      }
      /* ** Step switch specific callback ** */
      onUserIdentityChange(!isOpen);
    };

    /* ** Legal switch callback ** */
    const handleIdentityChange = ({ next }: SwitchIdentityTypeChange): void => {
      if (next === IdentityType.COMPANY) {
        onClose();
      }
      setIdType(next);
      onIdentityTypeChange(next);
    };

    /* ** Submit whatever representant form is being rendered ** */
    const handleFormSubmit = (data: DraftRepresentative): void => {
      onSubmit(data);
    };

    /* ** Cancel whatever representant form is being rendered ** */
    const handleFormCancel = (): void => {
      onCancel();
    };

    return (
      <Stack spacing="4">
        <SwitchIdentityType
          direction={{ base: 'column', md: 'row' }}
          value={idType}
          onChange={handleIdentityChange}
        />
        <Stack direction={{ base: 'column', md: 'row' }}>
          <FormControl alignItems="center" display="flex">
            <Switch
              id={`isUser${id}`}
              isChecked={isOpen}
              isDisabled={shouldDisabledSwitches}
              onChange={handleUserIdentityChange}
            />
            <FormLabel htmlFor={`isUser${id}`} mb="0" ml="2">
              <Text>
                {i18nGeneral('ItsMe')},
                <Text as="span" fontWeight="bold">
                  {` ${user?.firstName} ${user?.lastName}`}
                </Text>
              </Text>
            </FormLabel>
          </FormControl>
        </Stack>
        {idType === IdentityType.COMPANY ? (
          <LegalRepresentantInformationsForm
            defaultValues={{ ...defaultValues, type: IdentityType.COMPANY }}
            ref={ref}
            roles={roles}
            shouldCancel={shouldCancel}
            shouldDisplayRoles={shouldDisplayRoles}
            onCancel={handleFormCancel}
            onSubmit={handleFormSubmit}
          />
        ) : (
          <NaturalRepresentantInformationsForm
            defaultValues={{ ...defaultValues, type: IdentityType.INDIVIDUAL }}
            ref={ref}
            roles={roles}
            shouldCancel={shouldCancel}
            shouldDisplayRoles={shouldDisplayRoles}
            onCancel={handleFormCancel}
            onSubmit={handleFormSubmit}
          />
        )}
      </Stack>
    );
  },
);

RepresentantInformationsForm.displayName = 'RepresentantInformationsForm';

export type RepresentantInformationsFormProps = Props;
